<template>
  <div class="content-wrapper">
    <div class="content-body">

      <!-- ACCESS GUARD -->
      <div
        v-if="!$can('CHANTIER:admin')"
        class="chantiers-admin-empty chantiers-admin-empty--error"
      >
        <feather-icon
          icon="LockIcon"
          size="32"
        />
        <p class="mt-1 mb-0">
          Vous n'avez pas les droits pour accéder à cette page.
        </p>
      </div>

      <template v-else>
        <!-- TOOLBAR -->
        <div class="chantiers-admin-toolbar">
          <div class="chantiers-admin-toolbar__title">
            <feather-icon
              icon="GitBranchIcon"
              size="18"
              class="mr-50"
            />
            <strong>Origines de chantier</strong>
          </div>
          <b-button
            variant="primary"
            @click="openCreate"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-50"
            />
            Nouvelle origine
          </b-button>
        </div>

        <!-- LOADING -->
        <div
          v-if="loading"
          class="chantiers-admin-empty"
        >
          <b-spinner class="mb-1" />
          <p class="mt-1 mb-0">
            Chargement des origines…
          </p>
        </div>

        <!-- ERROR -->
        <div
          v-else-if="error"
          class="chantiers-admin-empty chantiers-admin-empty--error"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="32"
          />
          <p class="mt-1 mb-1">
            {{ error }}
          </p>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="loadOrigines"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              class="mr-50"
            />
            Réessayer
          </b-button>
        </div>

        <!-- EMPTY -->
        <div
          v-else-if="origines.length === 0"
          class="chantiers-admin-empty"
        >
          <feather-icon
            icon="InboxIcon"
            size="32"
          />
          <p class="mt-1 mb-0">
            Aucune origine pour le moment.
          </p>
        </div>

        <!-- TABLE -->
        <div
          v-else
          class="chantiers-admin-card"
        >
          <b-table
            :items="origines"
            :fields="fields"
            responsive
            hover
            class="mb-0"
          >
            <template #cell(actions)="data">
              <b-button
                variant="flat-secondary"
                size="sm"
                @click="openEdit(data.item)"
              >
                <feather-icon
                  icon="Edit2Icon"
                  size="14"
                />
              </b-button>
              <b-button
                variant="flat-danger"
                size="sm"
                @click="confirmDelete(data.item)"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="14"
                />
              </b-button>
            </template>
          </b-table>
        </div>
      </template>

      <!-- CREATE / EDIT MODAL -->
      <b-modal
        id="origine-modal"
        :title="editing ? 'Modifier l\'origine' : 'Nouvelle origine'"
        :ok-title="editing ? 'Enregistrer' : 'Créer'"
        cancel-title="Annuler"
        cancel-variant="outline-secondary"
        :ok-disabled="saving || !form.libelle"
        :busy="saving"
        @ok.prevent="submit"
      >
        <b-form-group
          label="Libellé"
          label-for="origine-libelle"
        >
          <b-form-input
            id="origine-libelle"
            v-model="form.libelle"
            placeholder="Ex. Audit ISO"
            :disabled="saving"
          />
        </b-form-group>
      </b-modal>

    </div>
  </div>
</template>

<script>
import {
  BButton,
  BFormGroup,
  BFormInput,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { successAlert, errorAlert } from '@/libs/sweetAlerts/alerts'

export default {
  name: 'OriginesChantierAdmin',
  components: {
    BButton,
    BFormGroup,
    BFormInput,
    BModal,
    BSpinner,
    BTable,
  },
  data() {
    return {
      origines: [],
      loading: true,
      error: null,
      saving: false,
      editing: null,
      form: { libelle: '' },
      fields: [
        { key: 'libelle', label: 'Libellé' },
        { key: 'actions', label: 'Actions', class: 'text-right' },
      ],
    }
  },
  created() {
    if (this.$can('CHANTIER:admin')) {
      this.loadOrigines()
    } else {
      this.loading = false
    }
  },
  methods: {
    async loadOrigines() {
      this.loading = true
      this.error = null
      try {
        const response = await this.$http.myressif.chantiers.listOrigines()
        this.origines = (response && response.items) || []
      } catch (e) {
        this.origines = []
        this.error = 'Une erreur est survenue lors du chargement des origines.'
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editing = null
      this.form = { libelle: '' }
      this.$bvModal.show('origine-modal')
    },
    openEdit(item) {
      this.editing = item
      this.form = { libelle: item.libelle }
      this.$bvModal.show('origine-modal')
    },
    async submit() {
      if (!this.form.libelle) return
      this.saving = true
      try {
        const payload = { libelle: this.form.libelle }
        if (this.editing) {
          await this.$http.myressif.chantiers.updateOrigine(this.editing.id, payload)
        } else {
          await this.$http.myressif.chantiers.createOrigine(payload)
        }
        this.$bvModal.hide('origine-modal')
        successAlert.fire({
          title: this.editing ? 'Origine modifiée' : 'Origine créée',
          text: 'Opération effectuée avec succès.',
        })
        await this.loadOrigines()
      } catch (e) {
        errorAlert.fire({
          title: 'Oops',
          text: 'L\'enregistrement de l\'origine a échoué.',
        })
      } finally {
        this.saving = false
      }
    },
    confirmDelete(item) {
      this.$bvModal
        .msgBoxConfirm(`Êtes-vous sûr de vouloir supprimer l'origine « ${item.libelle} » ?`, {
          title: 'Confirmation de suppression',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Supprimer',
          cancelTitle: 'Annuler',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => {
          if (value === true) this.doDelete(item)
        })
    },
    async doDelete(item) {
      try {
        await this.$http.myressif.chantiers.deleteOrigine(item.id)
        successAlert.fire({
          title: 'Origine supprimée',
          text: 'Suppression effectuée avec succès.',
        })
        await this.loadOrigines()
      } catch (e) {
        errorAlert.fire({
          title: 'Oops',
          text: 'La suppression de l\'origine a échoué.',
        })
      }
    },
  },
}
</script>

<style scoped lang="scss">
$at-green:   #1c4d4d;
$at-orange:  #ff6131;
$at-ink-500: #5A7373;
$at-ink-100: #E4ECEC;

.chantiers-admin-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;

  &__title {
    display: inline-flex;
    align-items: center;
    color: $at-green;
    font-size: 16px;
  }
}

.chantiers-admin-card {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  overflow: hidden;
}

.chantiers-admin-empty {
  text-align: center;
  padding: 48px 16px;
  color: $at-ink-500;
  background: #fff;
  border: 1px dashed $at-ink-100;
  border-radius: 10px;

  &--error {
    color: $at-orange;
    border-color: rgba(255, 97, 49, .3);
  }
}
</style>
